import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Api from '../../Vendor/Api'
import Collection, { getShortcut } from '../../Config/Collection'
import Filter from '../../Entity/Collection/ListingSetting/Filter'
import Choice from '../../Entity/Property/Choice'
import useTranslationDataUtils from '../../Vendor/Utils/TranslationDataUtils'
import { FilterValues, GlobalFilterValues } from '../Listing/Listing'

export interface FilterChoicesModalProps {
    show: boolean
    setShow: (show: boolean) => void
    filter: Filter
    entity?: Collection
    id?: number
    filterValues?: FilterValues
    setFilterValues: (filterValues: FilterValues) => void
    globalFilterValues?: GlobalFilterValues
}

interface ChoiceItem {
    id: string
    text: string
    isNotAttributed?: boolean
}

const isNotToken = (v: any) => typeof v === 'string' && v.startsWith('not:')

const FilterChoicesModal: FC<FilterChoicesModalProps> = ({
    show,
    setShow,
    filter,
    entity,
    id,
    filterValues,
    setFilterValues,
    globalFilterValues,
}) => {
    const { t } = useTranslation()
    const { translate, translateField } = useTranslationDataUtils()

    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [choices, setChoices] = useState<ChoiceItem[]>([])
    const [search, setSearch] = useState<string>('')
    const [selected, setSelected] = useState<FilterValues>({})
    const [activeLetter, setActiveLetter] = useState<string | null>(null)

    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const propertyLabel = filter.property
        ? translate(filter.property, 'label')
        : t(`app.${filter.filters}`)

    useEffect(() => {
        if (!show) {
            return
        }

        setSearch('')
        setSelected(filterValues ? { ...filterValues } : {})
        setError(null)
        setActiveLetter(null)

        if (!entity) {
            return
        }

        const shortcut = getShortcut(entity)
        if (!shortcut) {
            return
        }

        const slug = ['crm', 'shopping'].includes(shortcut) ? entity : id
        const url = `/listing/${shortcut}/${slug}/filter/${filter.slug}/choices`

        const appliedFilters = Object.fromEntries(
            Object.entries(globalFilterValues || {})
                .map(([key, value]) => {
                    if (!value || typeof value !== 'object') return undefined
                    const keysOnly = Object.keys(value)
                    return keysOnly.length ? [key, keysOnly] : undefined
                })
                .filter((entry): entry is [string, string[]] => !!entry)
        )

        setLoading(true)
        Api.post(url, {
            filters: appliedFilters,
            all: true,
        })
            .then((response) => {
                const data = response.data || {}
                const items: ChoiceItem[] = []

                if (filter.property?.type !== 'combined') {
                    items.push({
                        id: `not:${filter.property?.name || filter.slug}`,
                        text: t('app.not_attributed', {
                            property: propertyLabel,
                        }),
                        isNotAttributed: true,
                    })
                }

                if (Array.isArray(data.choices)) {
                    data.choices.forEach((choice: Choice) => {
                        const label =
                            typeof choice.key === 'string'
                                ? choice.key.includes('.')
                                    ? t(choice.key)
                                    : choice.key
                                : choice.key
                                ? translateField(choice.key, 'label')
                                : choice.value
                        items.push({
                            id: String(choice.value),
                            text: String(label),
                        })
                    })
                }

                setChoices(items)
            })
            .catch(() => {
                setError(t('app.error_occurred', 'Une erreur est survenue lors du chargement des options.'))
            })
            .finally(() => {
                setLoading(false)
            })
    }, [show, entity, id, filter.slug])

    const handleToggle = (item: ChoiceItem) => {
        const next: FilterValues = { ...selected }

        if (item.isNotAttributed) {
            if (next[item.id] !== undefined) {
                delete next[item.id]
            } else {
                Object.keys(next).forEach((k) => delete next[k])
                next[item.id] = item.text
            }
            setSelected(next)
            return
        }

        Object.keys(next).forEach((k) => {
            if (isNotToken(k)) {
                delete next[k]
            }
        })

        if (next[item.id] !== undefined) {
            delete next[item.id]
        } else {
            let label = ''
            if (filter.property?.type === 'checkbox') {
                label += translate(filter.property, 'label') + ': '
            }
            label += item.text
            next[item.id] = label
        }

        setSelected(next)
    }

    const filteredChoices = useMemo(() => {
        if (!search.trim()) {
            return choices
        }
        const term = search.toLowerCase()
        return choices.filter((item) => item.text.toLowerCase().includes(term))
    }, [choices, search])

    const groupedChoices = useMemo(() => {
        const notAttributedItems = filteredChoices.filter((c) => c.isNotAttributed)
        const regularItems = filteredChoices.filter((c) => !c.isNotAttributed)

        const groups: Record<string, ChoiceItem[]> = {}
        regularItems.forEach((item) => {
            const firstChar = (item.text.trim()[0] || '#').toUpperCase()
            const key = /^[A-Z0-9]$/.test(firstChar) ? firstChar : '#'
            if (!groups[key]) {
                groups[key] = []
            }
            groups[key].push(item)
        })

        const sortedKeys = Object.keys(groups).sort((a, b) => {
            if (a === '#') return 1
            if (b === '#') return -1
            return a.localeCompare(b)
        })

        return {
            notAttributedItems,
            groups,
            sortedKeys,
        }
    }, [filteredChoices])

    const handleSelectAllFiltered = () => {
        const next: FilterValues = { ...selected }
        Object.keys(next).forEach((k) => {
            if (isNotToken(k)) {
                delete next[k]
            }
        })
        filteredChoices.forEach((item) => {
            if (!item.isNotAttributed) {
                let label = ''
                if (filter.property?.type === 'checkbox') {
                    label += translate(filter.property, 'label') + ': '
                }
                label += item.text
                next[item.id] = label
            }
        })
        setSelected(next)
    }

    const handleClearSelection = () => {
        setSelected({})
    }

    const handleApply = () => {
        setFilterValues(selected)
        setShow(false)
    }

    const scrollToLetter = (letter: string) => {
        setActiveLetter(letter)
        const element = document.getElementById(`letter-section-${letter}`)
        if (element && scrollContainerRef.current) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }

    const selectedCount = Object.values(selected).filter((v) => v !== undefined).length

    const allRegularFiltered = filteredChoices.filter((c) => !c.isNotAttributed)
    const allRegularSelected = allRegularFiltered.every((c) => selected[c.id] !== undefined)

    return (
        <Modal
            show={show}
            onHide={() => setShow(false)}
            centered
            size="xl"
            className="filter-choices-modal"
        >
            {/* ── Header ── */}
            <div className="modal-header">
                <div className="modal-title">
                    <span className="title-icon">
                        <i className="fas fa-sliders"></i>
                    </span>
                    <span>{propertyLabel}</span>
                </div>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShow(false)}
                />
            </div>

            {/* ── Toolbar: search + select toggle ── */}
            <div className="filter-choices-toolbar">
                <div className="search-input-wrapper">
                    <i className="fas fa-search search-icon"></i>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={t('app.search', 'Rechercher...')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                    {search && (
                        <button
                            type="button"
                            className="search-clear-btn"
                            onClick={() => setSearch('')}
                            title={t('app.clear', 'Effacer')}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    )}
                </div>

                <div className="toolbar-actions">
                    {selectedCount > 0 && (
                        <span className="selected-badge">
                            <i className="fas fa-check-circle"></i>
                            {selectedCount} {t('app.selected', 'sélectionné(s)')}
                        </span>
                    )}
                    <button
                        type="button"
                        className="action-pill-btn"
                        onClick={allRegularSelected && selectedCount > 0 ? handleClearSelection : handleSelectAllFiltered}
                        disabled={loading || allRegularFiltered.length === 0}
                    >
                        {allRegularSelected && selectedCount > 0 ? (
                            <>
                                <i className="fas fa-times"></i>
                                {t('app.deselect_all', 'Tout désélectionner')}
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check-double"></i>
                                {t('app.selectAll', 'Tout sélectionner')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Alphabet nav — only when not searching ── */}
            {!search && groupedChoices.sortedKeys.length > 1 && (
                <div className="letter-nav-strip">
                    {groupedChoices.sortedKeys.map((letter) => (
                        <button
                            key={letter}
                            type="button"
                            className={`letter-btn ${activeLetter === letter ? 'active' : ''}`}
                            onClick={() => scrollToLetter(letter)}
                        >
                            {letter}
                        </button>
                    ))}
                </div>
            )}

            {/* ── Body ── */}
            <div className="modal-body" ref={scrollContainerRef}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="dark" style={{ width: '2rem', height: '2rem' }} />
                        <p className="mt-3 text-muted small fw-medium">{t('app.loading', 'Chargement...')}</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger py-2">{error}</div>
                ) : filteredChoices.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fas fa-search fa-2x mb-2 opacity-25"></i>
                        <p className="mb-0 fw-medium">{t('app.no_result', 'Aucun résultat trouvé')}</p>
                    </div>
                ) : (
                    <>
                        {/* Not-attributed sits in its own labeled section */}
                        {groupedChoices.notAttributedItems.length > 0 && (
                            <div id="letter-section-not-attributed" className="mb-3">
                                <div className="section-letter-header">
                                    <span className="letter-badge letter-badge--special">
                                        <i className="fas fa-ban"></i>
                                    </span>
                                    <div className="letter-divider" />
                                </div>
                                {groupedChoices.notAttributedItems.map((item) => {
                                    const isChecked = selected[item.id] !== undefined
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => handleToggle(item)}
                                            className={`not-attributed-card ${isChecked ? 'selected' : ''}`}
                                        >
                                            <div className={`card-checkbox ${isChecked ? 'checked' : ''}`}>
                                                {isChecked && <i className="fas fa-check"></i>}
                                            </div>
                                            <span className="card-label">{item.text}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {groupedChoices.sortedKeys.map((letter) => {
                            const items = groupedChoices.groups[letter]
                            return (
                                <div key={letter} id={`letter-section-${letter}`} className="mb-3">
                                    <div className="section-letter-header">
                                        <span className="letter-badge">{letter}</span>
                                        <span className="letter-count">({items.length})</span>
                                        <div className="letter-divider" />
                                    </div>
                                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-2">
                                        {items.map((item) => {
                                            const isChecked = selected[item.id] !== undefined
                                            return (
                                                <div key={item.id} className="col">
                                                    <div
                                                        onClick={() => handleToggle(item)}
                                                        className={`choice-card ${isChecked ? 'selected' : ''}`}
                                                    >
                                                        <div className={`card-checkbox ${isChecked ? 'checked' : ''}`}>
                                                            {isChecked && <i className="fas fa-check"></i>}
                                                        </div>
                                                        <span className="card-text" title={item.text}>
                                                            {item.text}
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </>
                )}
            </div>

            {/* ── Footer ── */}
            <div className="modal-footer">
                <div className="footer-counter">
                    {selectedCount > 0 ? (
                        <>
                            <strong>{selectedCount}</strong> {t('app.selected', 'sélectionné(s)')} / {choices.length}
                        </>
                    ) : (
                        <>
                            <strong>{choices.length}</strong> {t('app.options', 'options disponibles')}
                        </>
                    )}
                </div>
                <div className="footer-actions">
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => setShow(false)}
                    >
                        {t('app.cancel', 'Annuler')}
                    </button>
                    <button
                        type="button"
                        className="btn-validate"
                        onClick={handleApply}
                    >
                        <i className="fas fa-check"></i>
                        {t('app.validate', 'Valider')}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default FilterChoicesModal
